import { LitElement, html, nothing } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { currentLang, t } from '../i18n';
import { api } from '../api';
import { showToast } from '../toast';
import { defineZenComponent } from './register';
import { inviteAcceptErrorMessageKey, resolveAcceptanceMode } from '../invite-accept-logic';
import type { WorkspaceInvitationAcceptanceMode, WorkspaceInvitationInspection } from '../types';

type InviteAcceptPhase = 'closed' | 'loading' | 'ready' | 'confirm_delete' | 'submitting' | 'done' | 'error';

interface InviteAcceptState {
  phase: InviteAcceptPhase;
  token: string;
  inspection: WorkspaceInvitationInspection | null;
  selectedMode: WorkspaceInvitationAcceptanceMode | null;
  errorKey: string;
}

const ROLE_LABEL_KEY: Record<WorkspaceInvitationInspection['invitation']['role'], string> = {
  admin: 'workspaceRoleAdmin',
  member: 'workspaceRoleMember',
  guest: 'workspaceRoleGuest',
};

const RELOAD_DELAY_MS = 1200;

function closedState(): InviteAcceptState {
  return { phase: 'closed', token: '', inspection: null, selectedMode: null, errorKey: '' };
}

// The landing surface for an invitation mail link (`#accept-invite?token=…`).
// It previews the invitation, lets the invitee choose what happens to their own
// workspace data when there is any, and calls the accept API once.
export class ZenInviteAccept extends LitElement {
  static properties = {
    _state: { state: true },
  };

  declare _state: InviteAcceptState;

  private requestGeneration = 0;

  constructor() {
    super();
    this._state = closedState();
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    this.removeEventListener('keydown', this.onKeyDown);
    super.disconnectedCallback();
  }

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (this._state.phase === 'confirm_delete') {
      event.preventDefault();
      this._state = { ...this._state, phase: 'ready' };
      return;
    }
    if (this._state.phase === 'ready' || this._state.phase === 'error') {
      event.preventDefault();
      this.close();
    }
  };

  private isCurrentRequest(generation: number): boolean {
    return generation === this.requestGeneration && this.isConnected;
  }

  async open(token: string) {
    const generation = ++this.requestGeneration;
    this._state = { ...closedState(), phase: 'loading', token };
    try {
      const res = await api('/api/workspaces/invitations/inspect', { method: 'POST', json: { invite_token: token } });
      if (!this.isCurrentRequest(generation)) return;
      const inspection: WorkspaceInvitationInspection = {
        invitation: res.invitation,
        existing_data: res.existing_data,
        requires_mode_choice: res.requires_mode_choice === true,
      };
      this._state = { ...this._state, phase: 'ready', inspection };
    } catch (err) {
      if (!this.isCurrentRequest(generation)) return;
      this._state = { ...this._state, phase: 'error', errorKey: inviteAcceptErrorMessageKey((err as { code?: string } | null)?.code) };
    }
  }

  // Drops the token from the address bar so a reload or a shared URL does not
  // carry it, then hides the dialog.
  close() {
    this.requestGeneration += 1;
    this._state = closedState();
    if (location.hash.startsWith('#accept-invite')) {
      history.replaceState(null, '', `${location.pathname}${location.search}`);
    }
  }

  private _selectMode(mode: WorkspaceInvitationAcceptanceMode) {
    this._state = { ...this._state, selectedMode: mode };
  }

  private _submit() {
    const inspection = this._state.inspection;
    if (!inspection || this._state.phase !== 'ready') return;
    const mode = resolveAcceptanceMode(inspection, this._state.selectedMode);
    if (!mode) return;
    if (mode === 'delete_existing' && inspection.requires_mode_choice) {
      this._state = { ...this._state, phase: 'confirm_delete' };
      return;
    }
    void this._accept(mode);
  }

  private async _accept(mode: WorkspaceInvitationAcceptanceMode) {
    const generation = this.requestGeneration;
    const inspection = this._state.inspection;
    if (!inspection) return;
    this._state = { ...this._state, phase: 'submitting' };
    try {
      await api('/api/workspaces/invitations/accept', { method: 'POST', json: { invite_token: this._state.token, mode } });
      if (!this.isCurrentRequest(generation)) return;
      this._state = { ...this._state, phase: 'done' };
      showToast(t('inviteAcceptDone', { workspace: inspection.invitation.workspace_name }) as string, 'info');
      history.replaceState(null, '', `${location.pathname}${location.search}`);
      // Workspace membership changed under the whole app: goals, settings, and
      // capabilities all belong to the new workspace now.
      setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    } catch (err) {
      if (!this.isCurrentRequest(generation)) return;
      this._state = { ...this._state, phase: 'error', errorKey: inviteAcceptErrorMessageKey((err as { code?: string } | null)?.code) };
    }
  }

  private _renderSummary(inspection: WorkspaceInvitationInspection) {
    const inv = inspection.invitation;
    const role = t(ROLE_LABEL_KEY[inv.role]) as string;
    const workspace = inv.workspace_name || t('inviteAcceptWorkspaceFallback') as string;
    const summary = inv.inviter_name
      ? t('inviteAcceptSummary', { inviter: inv.inviter_name, workspace, role })
      : t('inviteAcceptSummaryNoInviter', { workspace, role });
    return html`
      <div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:12px;">
        <span class="mi" style="color:var(--accent);">group_add</span>
        <div>
          <div style="font-size:14px;line-height:1.5;">${summary}</div>
          <div style="font-size:12px;color:var(--text-muted);margin-top:4px;">${t('workspaceInviteExpiresAt', { date: new Date(inv.expires_at).toLocaleDateString(currentLang) })}</div>
        </div>
      </div>
    `;
  }

  private _renderModeOption(mode: WorkspaceInvitationAcceptanceMode, icon: string, titleKey: string, descKey: string, workspace: string) {
    const selected = this._state.selectedMode === mode;
    return html`
      <button
        type="button"
        class="invite-accept-mode"
        role="radio"
        aria-checked=${selected ? 'true' : 'false'}
        style=${`display:flex;align-items:flex-start;gap:10px;width:100%;text-align:left;padding:10px 12px;margin-bottom:8px;border-radius:var(--radius);cursor:pointer;background:var(--surface);border:1px solid ${selected ? 'var(--accent)' : 'var(--border)'};`}
        @click=${() => this._selectMode(mode)}
      >
        <span class="mi mi-sm" style=${`color:${selected ? 'var(--accent)' : 'var(--text-muted)'};`}>${selected ? 'radio_button_checked' : 'radio_button_unchecked'}</span>
        <span style="flex:1;">
          <span style="display:flex;align-items:center;gap:6px;font-size:13px;font-weight:500;"><span class="mi mi-sm" style=${`color:${mode === 'delete_existing' ? 'var(--danger)' : 'var(--accent)'};`}>${icon}</span>${t(titleKey)}</span>
          <span style="display:block;font-size:12px;color:var(--text-muted);margin-top:2px;">${t(descKey, { workspace })}</span>
        </span>
      </button>
    `;
  }

  private _renderReady(inspection: InviteAcceptState['inspection']) {
    if (!inspection) return nothing;
    const workspace = inspection.invitation.workspace_name || t('inviteAcceptWorkspaceFallback') as string;
    const canSubmit = resolveAcceptanceMode(inspection, this._state.selectedMode) !== null;
    return html`
      ${this._renderSummary(inspection)}
      ${inspection.requires_mode_choice ? html`
        <div style="font-size:13px;line-height:1.5;margin-bottom:10px;">
          ${t('inviteAcceptExistingData', { goals: inspection.existing_data.goal_count, events: inspection.existing_data.event_count })}
        </div>
        <div role="radiogroup" aria-label=${t('inviteAcceptModeLabel') as string}>
          ${this._renderModeOption('migrate_existing', 'drive_file_move', 'inviteAcceptModeMigrate', 'inviteAcceptModeMigrateDesc', workspace)}
          ${this._renderModeOption('delete_existing', 'delete', 'inviteAcceptModeDelete', 'inviteAcceptModeDeleteDesc', workspace)}
        </div>
      ` : html`
        <div style="font-size:13px;color:var(--text-muted);line-height:1.5;margin-bottom:10px;">${t('inviteAcceptEmpty')}</div>
      `}
      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
        <button type="button" class="btn btn-secondary" @click=${() => this.close()}>${t('inviteAcceptCancelBtn')}</button>
        <button type="button" class="btn btn-primary" ?disabled=${!canSubmit} @click=${() => this._submit()}>${t('inviteAcceptSubmitBtn')}</button>
      </div>
    `;
  }

  private _renderDeleteConfirm(inspection: InviteAcceptState['inspection']) {
    if (!inspection) return nothing;
    return html`
      <div role="alertdialog" aria-modal="true" aria-labelledby="inviteAcceptDeleteConfirmTitle" tabindex="-1">
        <div class="confirm-icon"><span class="mi" style="color:var(--danger);">delete_forever</span></div>
        <div class="confirm-msg" id="inviteAcceptDeleteConfirmTitle">${t('inviteAcceptDeleteConfirm', { goals: inspection.existing_data.goal_count, events: inspection.existing_data.event_count })}</div>
        <div class="confirm-actions">
          <button type="button" class="confirm-cancel" @click=${() => { this._state = { ...this._state, phase: 'ready' }; }}>${t('inviteAcceptCancelBtn')}</button>
          <button type="button" class="confirm-ok" @click=${() => this._accept('delete_existing')}>${t('inviteAcceptDeleteConfirmBtn')}</button>
        </div>
      </div>
    `;
  }

  private _renderBody() {
    const s = this._state;
    switch (s.phase) {
      case 'loading':
      case 'submitting':
        return html`
          <div style="display:flex;align-items:center;justify-content:center;gap:10px;padding:24px;color:var(--text-muted);font-size:13px;">
            <span class="spinner"></span>${t(s.phase === 'loading' ? 'inviteAcceptLoading' : 'inviteAcceptSubmitting')}
          </div>
        `;
      case 'ready':
        return this._renderReady(s.inspection);
      case 'confirm_delete':
        return this._renderDeleteConfirm(s.inspection);
      case 'done':
        return html`
          <div style="display:flex;align-items:center;gap:10px;padding:16px 0;font-size:14px;">
            <span class="mi" style="color:var(--success);">check_circle</span>
            ${t('inviteAcceptDone', { workspace: s.inspection?.invitation.workspace_name || t('inviteAcceptWorkspaceFallback') as string })}
          </div>
        `;
      case 'error':
        return html`
          <div class="msg msg-error show" role="alert" style="margin-bottom:12px;">
            <span class="mi mi-sm" style="vertical-align:-3px;" aria-hidden="true">error</span> ${t(s.errorKey)}
          </div>
          <div style="display:flex;justify-content:flex-end;">
            <button type="button" class="btn btn-secondary" @click=${() => this.close()}>${t('closeLabel')}</button>
          </div>
        `;
      default:
        return nothing;
    }
  }

  render() {
    const open = this._state.phase !== 'closed';
    // The delete confirmation reuses the app's confirm-modal styling, which is
    // scoped to an overlay carrying the confirm-modal class.
    const confirming = this._state.phase === 'confirm_delete';
    return keyed(currentLang, html`
      <div
        class=${confirming ? 'modal-overlay confirm-modal' : 'modal-overlay'}
        role="dialog"
        aria-modal="true"
        aria-labelledby=${confirming ? 'inviteAcceptDeleteConfirmTitle' : 'inviteAcceptTitle'}
        style=${open ? 'display:flex' : 'display:none'}
      >
        <div class="modal-content" style=${confirming ? '' : 'max-width:480px;'}>
          ${confirming ? nothing : html`
            <div class="modal-header">
              <h2 id="inviteAcceptTitle">${t('inviteAcceptTitle')}</h2>
            </div>
          `}
          <div class="modal-body">${this._renderBody()}</div>
        </div>
      </div>
    `);
  }
}

defineZenComponent('zen-invite-accept', ZenInviteAccept);
